/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosError, AxiosResponse } from "axios";
import { useState } from "react";

export interface IUseAxiosRequestProps<T = any, D = any> {
  request: () => Promise<AxiosResponse<T, D>>;
}

export interface IUseAxiosRequest<T = any, D = any> {
  triggerRequest: () => Promise<void>;
  isRequesting: boolean;
  successResponse?: AxiosResponse<T, D>;
  errorResponse?: AxiosError;
}

export const useAxiosRequest = <T, D>({ request }: IUseAxiosRequestProps<T>): IUseAxiosRequest => {
  const [isRequesting, setIsRequesting] = useState<boolean>(false);
  const [successResponse, setSuccessResponse] = useState<AxiosResponse<T, D>>();
  const [errorResponse, setErrorResponse] = useState<AxiosError>();

  const triggerRequest = async (): Promise<void> => {
    setIsRequesting(true);

    await request()
      .then((response: AxiosResponse<T, D>) => {
        setSuccessResponse(response);
        setErrorResponse(undefined);
      })
      .catch((error: AxiosError) => {
        setSuccessResponse(undefined);
        setErrorResponse(error);
      })
      .finally(() => {
        setIsRequesting(false);
      });
  };

  return {
    triggerRequest,
    isRequesting,
    successResponse,
    errorResponse,
  };
};
